import React, { useCallback, useRef } from 'react';
import { Button, Empty } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { useP9306Query } from './hooks/useP9306Query';
import { FilterPanel } from './components/FilterPanel';
import { ResultTable } from './components/ResultTable';
import type { StaffingRow, DeptSummary, CompanyTotals } from '../../services/p9306Api';
import './P9306Form.css';

const P9306Form: React.FC = () => {
  const { loading, data, summary, totals, queried, noData, runQuery, reset, lookupDeptName } =
    useP9306Query();

  // Keep refs for print data
  const printDataRef = useRef<{
    data: StaffingRow[];
    summary: DeptSummary[];
    totals: CompanyTotals | null;
  }>({ data: [], summary: [], totals: null });
  printDataRef.current = { data, summary, totals };

  const handlePrint = useCallback(() => {
    const { data: d, summary: s, totals: t } = printDataRef.current;
    if (!d || d.length === 0) return;

    const win = window.open('', '_blank');
    if (!win) return;

    const detailRows = d
      .map(
        (r) =>
          `<tr><td>${r.dept_code}</td><td>${r.dept_name}</td><td>${r.title_code}</td><td>${r.title_name}</td><td class="right">${r.planned_count}</td><td class="right">${r.actual_count}</td><td class="right">${r.actual_count - r.planned_count}</td><td>${r.emp_no || ''}</td><td>${r.emp_name || ''}</td><td>${r.hire_date?.substring(0, 10) || ''}</td></tr>`
      )
      .join('');

    const summaryRows = s
      .map(
        (r) =>
          `<tr><td>${r.dept_code}</td><td>${r.dept_name}</td><td class="right">${r.dept_planned}</td><td class="right">${r.dept_approved}</td><td class="right">${r.total_planned}</td><td class="right">${r.total_actual}</td><td class="right">${r.total_actual - r.total_planned}</td></tr>`
      )
      .join('');

    const totalRow = t
      ? `<tr style="font-weight:bold;background:#f0f0f0"><td colspan="2">全公司合計（${t.total_depts} 個部門）</td><td class="right">${t.total_planned}</td><td class="right">${t.total_approved}</td><td class="right">—</td><td class="right">${t.total_actual}</td><td class="right">${t.total_actual - t.total_planned}</td></tr>`
      : '';

    win.document.write(`<!DOCTYPE html><html><head><title>部門編制明細表</title>
<style>body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:12px}h2,h3{text-align:center;margin:10px 0}table{width:100%;border-collapse:collapse;margin-bottom:20px}th,td{border:1px solid #333;padding:3px 6px}th{background:#f0f0f0;text-align:center}.right{text-align:right}@media print{body{margin:10mm}}</style></head><body>
<h2>部門編制明細表</h2>
<h3>編制明細</h3>
<table><thead><tr><th>部門代號</th><th>部門名稱</th><th>職稱代號</th><th>職稱名稱</th><th>編制人數</th><th>實際人數</th><th>差異</th><th>員工編號</th><th>員工姓名</th><th>到職日期</th></tr></thead><tbody>${detailRows}</tbody></table>
<h3>部門彙總</h3>
<table><thead><tr><th>部門代號</th><th>部門名稱</th><th>部門編制人數</th><th>部門核准人數</th><th>職稱編制合計</th><th>實際在職合計</th><th>差異</th></tr></thead><tbody>${summaryRows}${totalRow}</tbody></table>
</body></html>`);
    win.document.close();
    win.focus();
    win.print();
  }, []);

  return (
    <PermissionGuard module={Modules.PW9306} action="query">
      <div className="p9306-container">
        <div className="p9306-header">
          <h2>部門編制明細表</h2>
          {queried && !noData && (
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>
              列印
            </Button>
          )}
        </div>

        <FilterPanel
          loading={loading}
          onQuery={runQuery}
          onReset={reset}
          onLookupDept={lookupDeptName}
        />

        {queried && noData && <Empty description="查無符合條件的部門編制資料" style={{ marginTop: 40 }} />}

        {queried && !noData && (
          <ResultTable data={data} summary={summary} totals={totals} loading={loading} />
        )}
      </div>
    </PermissionGuard>
  );
};

export default P9306Form;
